import {
  IonAlert,
  IonBackButton,
  IonButtons,
  IonContent,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import type React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { cardOutline, closeCircle, closeOutline, contractOutline } from 'ionicons/icons';
import { useState } from 'react';
import type {
  Player,
  PublicFantasyLeagueMemberInfoQuery,
  PublicFantasyLeagueMemberInfoQueryVariables,
  GetFantasyLeagueMembersQuery,
  GetFantasyLeagueMembersQueryVariables,
  GetVisibleTransferOffersQuery,
  GetVisibleTransferOffersQueryVariables,
  WithdrawTransferOfferMutation,
  WithdrawTransferOfferMutationVariables,
} from '../../../types/graphql-generated';
import { PUBLIC_FANTASY_LEAGUE_MEMBER_INFO } from '../../../graphql/queries/fantasyLeagueMemberInfo';
import { GET_FANTASY_LEAGUE_MEMBERS } from '../../../graphql/queries/fantasyLeagueMembers';
import { GET_VISIBLE_TRANSFER_OFFERS } from '../../../graphql/queries/visibleTransferOffers';
import { WITHDRAW_TRANSFER_OFFER } from '../../../graphql/mutations/transferOffer';
import { PlayerCard } from '../components/PlayerCard';
import { PlaceBidModal, OfferTargetFactory } from '../components/PlaceBidModal';
import { PayReleaseClauseModal } from './components/PayReleaseClauseModal';
import { PlayerReleaseClauseChip } from '../components/PlayerReleaseClauseChip';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { UserAvatar } from '../../components/UserAvatar';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { useMarketHours } from '../../../hooks/useMarketHours';

interface Props {
  leagueId: string;
  defaultBackHref?: string;
}

type FantasyTeamPlayer = NonNullable<
  NonNullable<GetFantasyLeagueMembersQuery['fantasyLeague']>['members'][number]['fantasyTeamPlayers'][number]
>;

export const FantasyLeagueMemberTeamPage = ({
  leagueId,
  defaultBackHref = `/app/ligas/${leagueId}/puntos`,
}: Props) => {
  const fantasyLeagueMemberId = useParams<'fantasyLeagueMemberId'>().fantasyLeagueMemberId!;
  const { userAccount } = useUserAccount();
  const { closeTime, openTime } = useMarketHours();
  const [presentToast] = useIonToast();
  const ionRouter = useIonRouter();

  type ToastKind = 'success' | 'danger' | 'warning';
  const showToast = (kind: ToastKind, message: string) => {
    presentToast({
      message,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      ...(kind === 'warning'
        ? { color: 'warning' }
        : { cssClass: kind === 'success' ? 'toast-success' : 'toast-danger' }),
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isMakeOfferModalOpen, setIsMakeOfferModalOpen] = useState(false);
  const [withdrawAlertOpen, setWithdrawAlertOpen] = useState(false);
  const [offerToWithdraw, setOfferToWithdraw] = useState<string | null>(null);
  const [payClausePlayer, setPayClausePlayer] = useState<Player | null>(null);
  const [isPayClauseModalOpen, setIsPayClauseModalOpen] = useState(false);

  const { data, loading, error } = useQuery<
    PublicFantasyLeagueMemberInfoQuery,
    PublicFantasyLeagueMemberInfoQueryVariables
  >(PUBLIC_FANTASY_LEAGUE_MEMBER_INFO, {
    variables: { fantasyLeagueMemberId },
    pollInterval: 60000,
  });

  const { data: membersData } = useQuery<
    GetFantasyLeagueMembersQuery,
    GetFantasyLeagueMembersQueryVariables
  >(GET_FANTASY_LEAGUE_MEMBERS, {
    variables: { id: leagueId },
  });

  const { data: transferOffersData, refetch: refetchTransferOffers } = useQuery<
    GetVisibleTransferOffersQuery,
    GetVisibleTransferOffersQueryVariables
  >(GET_VISIBLE_TRANSFER_OFFERS, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: 60000,
  });

  const [withdrawTransferOffer] = useMutation<
    WithdrawTransferOfferMutation,
    WithdrawTransferOfferMutationVariables
  >(WITHDRAW_TRANSFER_OFFER, {
    refetchQueries: [
      { query: GET_VISIBLE_TRANSFER_OFFERS, variables: { fantasyLeagueId: leagueId } },
    ],
  });

  const member = data?.publicFantasyLeagueMemberInfo;

  const players = member?.players ?? [];
  const totalMarketValue = players.reduce((sum, p) => sum + (p.marketValue ?? 0), 0);

  const roleOrder = ['PORTERO', 'DEFENSA', 'CENTROCAMPISTA', 'DELANTERO', 'Sin posición'] as const;

  const playersByRole = [...(players ?? [])]
    .sort((a, b) => (b.marketValue ?? 0) - (a.marketValue ?? 0))
    .reduce((acc: Record<string, Player[]>, player) => {
      const role = player.role ?? 'Sin posición';
      if (!acc[role]) acc[role] = [];
      acc[role].push(player);
      return acc;
    }, {});

  const currentUserMember = membersData?.fantasyLeague?.members.find(
    (m) => m.user.id === userAccount?.id
  );

  const isOwnTeam = currentUserMember?.id === fantasyLeagueMemberId;
  const showOfferFooter = !isOwnTeam;

  const transferOffers = transferOffersData?.visibleTransferOffers ?? [];

  const opponentMember = membersData?.fantasyLeague?.members.find(
    (m) => m.id === fantasyLeagueMemberId
  );

  const displayTeamName = opponentMember?.teamName ?? member?.teamName;
  const displayUserName = opponentMember?.user.fullName;
  const title = displayTeamName ? `Plantilla - ${displayTeamName}` : 'Plantilla';

  const playerIdToFtp = new Map<string, FantasyTeamPlayer>(
    (opponentMember?.fantasyTeamPlayers ?? []).map(
      (ftp) => [ftp.player.id, ftp]
    )
  );

  const getExistingOffer = (playerId: string) =>
    transferOffers.find(
      (o) => o.player.id === playerId && o.bidder.id === currentUserMember?.id
    ) ?? null;

  const handleOpenOffer = (player: Player) => {
    setSelectedPlayer(player);
    setIsMakeOfferModalOpen(true);
  };

  const handleOpenPayClause = async (player: Player, ftp: FantasyTeamPlayer) => {
    if (!ftp.releaseClausePayable) {
      let message: string;
      if (ftp.individuallyProtected) {
        message = 'Este jugador está en período de protección individual.';
      } else if (ftp.weeklyWindowProtected) {
        message = 'Este jugador está protegido durante la ventana de la jornada.';
      } else {
        message = `El pago de cláusulas no está disponible entre las ${closeTime} y las ${openTime}.`;
      }
      showToast('warning', message);
      return;
    }
    setPayClausePlayer(player);
    setIsPayClauseModalOpen(true);
  };

  const handleWithdrawOffer = (offerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOfferToWithdraw(offerId);
    setWithdrawAlertOpen(true);
  };

  const confirmWithdrawOffer = async () => {
    if (!offerToWithdraw) return;
    try {
      await withdrawTransferOffer({ variables: { transferOfferId: offerToWithdraw } });
      setWithdrawAlertOpen(false);
      setOfferToWithdraw(null);
    } catch (err) {
      console.error('Error withdrawing transfer offer:', err);
      showToast('danger', 'Error al retirar la oferta');
      setWithdrawAlertOpen(false);
      setOfferToWithdraw(null);
    }
  };

  const buildPlayerFooter = (player: Player) => {
    if (!showOfferFooter) return undefined;
    const existingOffer = getExistingOffer(player.id);
    const ftp = playerIdToFtp.get(player.id);

    return {
      details: existingOffer ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-slate-500">Tu oferta</span>
          <span className="rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-900">
            {existingOffer.amount.toLocaleString('es-ES')}€
          </span>
        </div>
      ) : undefined,
      actions: [
        {
          label: existingOffer ? 'Modificar' : 'Fichar',
          icon: cardOutline,
          onClick: () => handleOpenOffer(player),
        },
        ...(ftp != null
          ? [{
              label: 'Pagar cláusula',
              icon: contractOutline,
              muted: !ftp.releaseClausePayable,
              onClick: () => handleOpenPayClause(player, ftp),
            }]
          : []),
        ...(existingOffer
          ? [{
              label: 'Retirar oferta',
              icon: closeCircle,
              onClick: (event: React.MouseEvent<HTMLButtonElement>) => handleWithdrawOffer(existingOffer.id, event),
              variant: 'danger' as const,
            }]
          : []),
      ],
    };
  };

  const getReleaseClauseDisplay = (player: Player) => {
    const ftp = playerIdToFtp.get(player.id);
    if (!ftp || ftp.releaseClause == null) return undefined;
    return (
      <PlayerReleaseClauseChip
        releaseClause={ftp.releaseClause}
        individuallyProtected={ftp.individuallyProtected}
        weeklyWindowProtected={ftp.weeklyWindowProtected}
        signedAt={ftp.signedAt ?? ftp.createdAt}
      />
    );
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={defaultBackHref} />
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent className="ion-padding">
        {loading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p>Error al cargar la plantilla</p>
          </IonText>
        )}

        {!loading && !error && !member && (
          <IonText color="medium">
            <p>No hay datos</p>
          </IonText>
        )}

        <div
          className="mb-4 rounded-2xl p-4 text-white shadow-[0_8px_22px_rgba(12,32,80,0.18)]"
          style={{ background: 'linear-gradient(135deg, #0a2a5b, #1a3d70)' }}
        >
          <div className="flex items-center gap-3">
            <UserAvatar
              picture={opponentMember?.user.picture}
              updatedAt={opponentMember?.user.updatedAt}
              name={displayUserName || displayTeamName}
              size={48}
              className="ring-2 ring-white/70"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate font-heading" style={{ fontSize: '18px', fontWeight: 800, lineHeight: 1.15 }}>
                {displayUserName || displayTeamName || 'Usuario'}
              </div>
              {displayUserName && displayTeamName && (
                <div className="mt-1 truncate text-sm font-semibold" style={{ color: '#b8c7e2' }}>
                  Equipo: {displayTeamName}
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 flex items-stretch gap-3">
            <div className="flex-1">
              <div className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: '#9fb2d4' }}>
                Jugadores
              </div>
              <div className="font-heading" style={{ fontSize: '20px', fontWeight: 800 }}>
                {players.length}
              </div>
            </div>
            <div className="w-px self-stretch" style={{ background: 'rgba(255,255,255,0.15)' }} />
            <div className="flex-1">
              <div className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: '#9fb2d4' }}>
                Valor total
              </div>
              <div className="font-heading" style={{ fontSize: '20px', fontWeight: 800 }}>
                {totalMarketValue.toLocaleString('es-ES')}€
              </div>
            </div>
          </div>
        </div>

        {!loading && !error && member && (
          <>
            {players.length === 0 ? (
              <IonText color="medium">
                <p>No hay jugadores en la plantilla</p>
              </IonText>
            ) : (
              <div className="space-y-4">
                {roleOrder.map((role) => {
                  const group = playersByRole[role];
                  if (!group || group.length === 0) return null;

                  return (
                    <div key={role} className="space-y-2">
                      {group.map((p) => {
                        const footer = buildPlayerFooter(p);
                        return (
                          <PlayerCard
                            key={p.id}
                            player={p}
                            releaseClauseDisplay={getReleaseClauseDisplay(p)}
                            footerDetails={footer?.details}
                            footerActions={footer?.actions}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {selectedPlayer && (
          <PlaceBidModal
            isOpen={isMakeOfferModalOpen}
            target={OfferTargetFactory.fromPlayer(selectedPlayer, currentUserMember?.budget ?? 0, getExistingOffer(selectedPlayer.id))}
            leagueId={leagueId}
            releaseClause={playerIdToFtp.get(selectedPlayer.id)?.releaseClause ?? undefined}
            onClose={() => {
              setIsMakeOfferModalOpen(false);
              setSelectedPlayer(null);
              refetchTransferOffers();
            }}
          />
        )}

        {payClausePlayer && (
          <PayReleaseClauseModal
            isOpen={isPayClauseModalOpen}
            player={payClausePlayer}
            releaseClause={playerIdToFtp.get(payClausePlayer.id)?.releaseClause ?? 0}
            budget={currentUserMember?.budget ?? 0}
            leagueId={leagueId}
            fantasyLeagueMemberId={fantasyLeagueMemberId}
            buyerMemberId={currentUserMember?.id ?? ''}
            onClose={() => {
              setIsPayClauseModalOpen(false);
              setPayClausePlayer(null);
            }}
            onSuccess={async () => {
              const nick = payClausePlayer?.nick;
              setIsPayClauseModalOpen(false);
              setPayClausePlayer(null);
              showToast('success', `¡Has fichado a ${nick}!`);
              ionRouter.goBack();
            }}
          />
        )}

        <IonAlert
          isOpen={withdrawAlertOpen}
          header="Retirar oferta directa"
          message="¿Estás seguro de que quieres retirar tu oferta directa?"
          buttons={[
            {
              text: 'Mantener',
              role: 'cancel',
              handler: () => {
                setWithdrawAlertOpen(false);
                setOfferToWithdraw(null);
              },
            },
            {
              text: 'Retirar',
              role: 'destructive',
              handler: confirmWithdrawOffer,
            },
          ]}
          onDidDismiss={() => {
            setWithdrawAlertOpen(false);
            setOfferToWithdraw(null);
          }}
        />
      </IonContent>
    </IonPage>
  );
};
